import { DueDiligencePageGetDueDiligenceRecordQuery, DueDiligenceRecordStatus, DueDiligenceRecordTierStatus } from '@/__generated__/graphql';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { ApolloQueryResult } from '@apollo/client';
import AdvanceTo from '../modals/advanceTo';
import BackTo from '../modals/backTo';
import ChangeAssignee from '../modals/changeAssignee';
import DownloadPDF from '../modals/downloadPDF';
import OverrideAnswer from '../modals/overrideAnswer';
import RecordStatus from '../modals/recordStatus';
import RelinkField from '../modals/relinkField';
import {
  DueDiligenceRecordTier,
  DueDiligenceRecordTierChecklistItem,
  DueDiligenceRecordTierSection,
  DueDiligenceRecordTierSectionField,
  ITier
} from './types';

type FieldModalProps = {
  modalWindow: { isOpen: boolean; type: string };
  currentRow:
    | DueDiligenceRecordTier
    | DueDiligenceRecordTierSection
    | DueDiligenceRecordTierSectionField
    | DueDiligenceRecordTierChecklistItem
    | null;
  currentField: IFieldEditor | null;
  recordId: string | null;
  activeTab: string;
  owner?: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['owner'];
  strategyId?: string;
  updateTierStatus: (variables: any) => void;
  advanceToNexTier: () => void;
  backToPreviousTier: (tierData: ITier) => void;
  openEditPage: (id: string, documentFieldType: string) => void;
  dueDiligenceRecordRefetch: () => Promise<ApolloQueryResult<DueDiligencePageGetDueDiligenceRecordQuery>>;
  closeModalWindow: () => void;
};

const FieldModal = ({
  modalWindow,
  currentRow,
  currentField,
  recordId,
  activeTab,
  owner,
  strategyId,
  updateTierStatus,
  advanceToNexTier,
  backToPreviousTier,
  openEditPage,
  dueDiligenceRecordRefetch,
  closeModalWindow
}: FieldModalProps) => {
  switch (modalWindow.type) {
    case 'change-assignee':
      return (
        <ChangeAssignee
          isOpen={modalWindow.isOpen}
          row={currentRow as DueDiligenceRecordTierSection | DueDiligenceRecordTierChecklistItem}
          owner={owner}
          dueDiligenceRecordRefetch={dueDiligenceRecordRefetch}
          onClose={closeModalWindow}
        />
      );
    case 'advance-to':
      return (
        <AdvanceTo
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          currentRow={currentRow}
          updateTierStatus={updateTierStatus}
          advanceToNexTier={advanceToNexTier}
        />
      );
    case 'reopen-diligence':
      return (
        <RecordStatus
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          recordId={recordId}
          strategyId={strategyId}
          recordStatus={DueDiligenceRecordStatus.Active}
          tierStatus={DueDiligenceRecordTierStatus.InProgress}
          skipReasonInput
          onClose={closeModalWindow}
        />
      );
    case 'approve-diligence':
      return (
        <RecordStatus
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          recordId={recordId}
          strategyId={strategyId}
          recordStatus={DueDiligenceRecordStatus.Approved}
          tierStatus={DueDiligenceRecordTierStatus.Completed}
          onClose={closeModalWindow}
        />
      );
    case 'reject-investment':
      return (
        <RecordStatus
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          recordId={recordId}
          strategyId={strategyId}
          recordStatus={DueDiligenceRecordStatus.Rejected}
          tierStatus={DueDiligenceRecordTierStatus.Rejected}
          onClose={closeModalWindow}
        />
      );
    case 'back-to':
      return <BackTo isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentRow={currentRow} backToPreviousTier={backToPreviousTier} />;
    case 'download-pdf':
      return <DownloadPDF isOpen={modalWindow.isOpen} onClose={closeModalWindow} activeTab={activeTab} recordId={recordId} />;
    case 'relink-field':
      return <RelinkField isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentField={currentField} recordId={recordId} />;
    case 'override-answer':
      return <OverrideAnswer isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentField={currentField} openEditPage={openEditPage} />;
    default:
      return <></>;
  }
};

export default FieldModal;
